<template>
    <div v-if="isPC" class="pc-container">
        <iframe ref="iframeRef" class="mobile-frame" :src="iframeSrc" frameborder="0"></iframe>
    </div>
    <div v-else id="app">
        <router-view v-slot="{ Component }">
            <keep-alive>
                <component :is="Component" :key="$route.name" v-if="$route.meta.keepAlive" />
            </keep-alive>
            <component :is="Component" :key="$route.name" v-if="!$route.meta.keepAlive" />
        </router-view>
        <floating-service></floating-service>
        <!-- 全局新客服抽屉弹窗（从底部滑出半屏，七鱼精致风格） -->
        <kf-drawer-popup></kf-drawer-popup>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { showToast } from 'vant'
import { weixin_login, user_info, active, jvAd, updateNickName } from './request/index'
import YSF from '@neysf/qiyu-web-sdk'
import './assets/css/pc.css'
import floatingService from './vue-pages/components/floating_service.vue'
import kfDrawerPopup from './vue-pages/components/kf_drawer_popup.vue'
import common from './uilts/common'

const router = useRouter()
const route = useRoute()
const webInfo = ref({})
const token = computed(() => localStorage.getItem('token') || '')
const uuid = computed(() => localStorage.getItem('uuid') || '')

// 设备检测
const isPC = ref(false)
const iframeSrc = ref(window.location.href)
const iframeRef = ref(null)
let checkCallback = null
let resizeTimeout = null
let isPaying = false // 支付状态标记
let initialDeviceType = null // 初始设备类型

// 防抖函数
const debounce = (func, wait) => {
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(resizeTimeout)
            func(...args)
        }
        clearTimeout(resizeTimeout)
        resizeTimeout = setTimeout(later, wait)
    }
}

// 检测设备类型
const checkDevice = () => {
    // 支付过程中不执行设备检测，避免切换导致白屏
    if (isPaying) {
        return
    }

    const width = window.innerWidth

    // 添加更大的缓冲区间，避免在 768 附近频繁切换
    if (width > 850) {
        isPC.value = true
    } else if (width < 650) {
        isPC.value = false
    }
    // 只有在设备类型变化时才更新 iframe src
    if (checkCallback) {
        checkCallback(isPC.value)
    }
}

// 防抖处理的 resize 事件
const debouncedCheckDevice = debounce(checkDevice, 500) // 增加防抖时间

// 检查是否在 iframe 中
const isInIframe = window.self !== window.top

// 全局支付状态管理
window.isAppPaying = false

// 监听全局支付状态变化
window.addEventListener('app-pay-status', (e) => {
    isPaying = e.detail.isPaying
    window.isAppPaying = e.detail.isPaying
})

// 如果不在 iframe 中，监听消息事件，同步路由
if (!isInIframe) {
    let lastChildPath = ''
    window.addEventListener('message', (e) => {
        const { type, path, isPaying: payingStatus } = e.data || {}

        // 处理支付状态
        if (type === 'app-pay-status') {
            isPaying = payingStatus
        }

        // 处理路由变化
        if (type === 'app-child-router-change') {
            if (path !== lastChildPath) {
                lastChildPath = path
                const targetUrl = router.resolve(path).href
                window.history.replaceState(null, '', targetUrl)
            }
        }
    })

    // 监听微信支付完成后的重定向
    window.addEventListener('load', () => {
        const urlParams = new URLSearchParams(window.location.search)
        const payResult = urlParams.get('payResult')
        if (payResult) {
            // 清除支付结果参数
            if (window.history.replaceState) {
                const url = new URL(window.location.href)
                url.searchParams.delete('payResult')
                window.history.replaceState({}, '', url.toString())
            }
            // 通知 iframe 支付结果
            if (iframeRef.value) {
                iframeRef.value.contentWindow.postMessage({ type: 'app-pay-result', result: payResult }, '*')
            }
            // 重置支付状态
            isPaying = false
        }
    })
}

// 如果在 iframe 中，监听路由变化，发送消息给父窗口
if (isInIframe) {
    router.afterEach((to) => {
        if (to.matched.length === 0) return
        window.parent.postMessage({ type: 'app-child-router-change', path: to.fullPath }, '*')
    })

    // 监听支付结果消息
    window.addEventListener('message', (e) => {
        const { type, result } = e.data || {}
        if (type === 'app-pay-result') {
            // 处理支付结果
            // 重置支付状态
            isPaying = false
            // 通知父窗口支付状态
            window.parent.postMessage({ type: 'app-pay-status', isPaying: false }, '*')
            // 可以在这里更新页面状态或跳转到订单页面
        }
    })

    // 监听支付开始/结束事件
    window.addEventListener('message', (e) => {
        const { type, isPaying: payingStatus } = e.data || {}
        if (type === 'app-pay-status') {
            isPaying = payingStatus
        }
    })
}



onMounted(() => {
    // 获取当前站点
    getWeb()
    // 检测设备类型
    checkCallback = (isPCDevice) => {
        // 支付过程中不更新 iframe src，避免白屏
        if (isPCDevice && !isPaying) {
            iframeSrc.value = window.location.href
        }
    }

    // 初始化设备类型
    checkDevice()
    initialDeviceType = isPC.value

    // 监听 resize 事件
    window.addEventListener('resize', debouncedCheckDevice)

    // 获取其他参数
    getOtherUrlParams()

    // 获取用户信息
    getUserInfo()
})

onUnmounted(() => {
    checkCallback = null
    window.removeEventListener('resize', debouncedCheckDevice)
    if (resizeTimeout) {
        clearTimeout(resizeTimeout)
    }
})
// 获取其他参数
const getOtherUrlParams = () => {
    const urlParams = new URLSearchParams(window.location.search)
    const uid = urlParams.get('uid')
    if (uid) {
        sessionStorage.setItem('uid', uid)
    }
    const media_id = urlParams.get('media_id')
    if (media_id) {
        sessionStorage.setItem('media_id', media_id)
    }
    const clickid = urlParams.get('clickid')
    if (clickid) {
        sessionStorage.setItem('clickid', clickid)
    }
    const adid = urlParams.get('adid')
    const creativeid = urlParams.get('creativeid')
    const creativetype = urlParams.get('creativetype')
    const jvAdParams = {}
    if (adid) jvAdParams.adid = adid
    if (creativeid) jvAdParams.creativeid = creativeid
    if (creativetype) jvAdParams.creativetype = creativetype
    if (clickid) jvAdParams.clickid = clickid
    if (media_id) jvAdParams.media_id = media_id
    if (isInIframe || !isPC.value) {
        jvAd(jvAdParams).then(res => {

        }).catch(err => {

        })
    }
}

// 获取当前站点（失败自动重试，避免接口偶发超时导致登录流程未触发）
const getWeb = async () => {
    const maxRetries = 3
    for (let i = 0; i < maxRetries; i++) {
        try {
            const webData = await common.getWeb()
            if (webData) {
                webInfo.value = webData
                configWeb()
                weixinLogin()
                if (webData.web_service_config == 2) {
                    initQiyu()
                }
                appActive()
                return
            }
        } catch (e) {
            // 请求异常，间隔后重试
        }
        if (i < maxRetries - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000))
        }
    }
}

// 激活事件
const appActive = () => {
    if (common.isApp() || common.isIosSafari()) {
        const hasActive = localStorage.getItem('app_active')
        if (!hasActive) {
            active({ type: 1 }).then(() => {
                localStorage.setItem('app_active', '1')
            }).catch(() => {})
        }
        setTimeout(() => {
            active({ type: 2 }).catch(() => {})
        }, 100)
    }
}

// 配置站点信息
const configWeb = () => {
    // 配置颜色
    document.documentElement.style.setProperty('--color1', webInfo.value.web_rgb_one)
    document.documentElement.style.setProperty('--color2', webInfo.value.web_rgb_two)
    document.documentElement.style.setProperty('--color3', webInfo.value.web_rgb_three)
    document.documentElement.style.setProperty('--color4', webInfo.value.web_rgb_four)
    document.documentElement.style.setProperty('--color5', webInfo.value.web_rgb_five)
    document.documentElement.style.setProperty('--color6', webInfo.value.web_rgb_six)
    document.documentElement.style.setProperty('--color7', webInfo.value.web_rgb_seven)
    document.documentElement.style.setProperty('--color8', webInfo.value.web_rgb_eight)
    document.documentElement.style.setProperty('--color9', webInfo.value.web_rgb_nine)
    document.documentElement.style.setProperty('--color10', webInfo.value.web_rgb_ten)
    document.documentElement.style.setProperty('--color11', webInfo.value.web_rgb_eleven)
    document.documentElement.style.setProperty('--color12', webInfo.value.web_rgb_twelve)
    document.documentElement.style.setProperty('--color13', webInfo.value.web_rgb_thirteen)
    document.documentElement.style.setProperty('--color14', webInfo.value.web_rgb_fourteen)
    document.documentElement.style.setProperty('--color15', webInfo.value.web_rgb_fiftyteen)
    document.documentElement.style.setProperty('--color16', webInfo.value.web_rgb_sixteen)
    document.documentElement.style.setProperty('--color17', webInfo.value.web_rgb_seventeen)

    // 配置网站标题icon
    let link = document.getElementById('favicon') || document.querySelector("link[rel*='icon']") || document.createElement('link')
    link.type = 'image/x-icon'
    link.rel = 'icon'
    link.href = webInfo.value.web_logo
    if (!document.getElementById('favicon') && !document.querySelector("link[rel*='icon']")) {
        document.head.appendChild(link)
    }

    // 配置苹果触摸图标
    const appleTouchIconSizes = ['', '76x76', '120x120', '152x152', '180x180']
    appleTouchIconSizes.forEach(size => {
        let appleLink = document.querySelector(`link[rel='apple-touch-icon'][sizes='${size}']`) ||
            document.querySelector(`link[rel='apple-touch-icon']${size ? '' : ':not([sizes])'}`) ||
            document.createElement('link')
        appleLink.rel = 'apple-touch-icon'
        if (size) {
            appleLink.sizes = size
        }
        appleLink.href = webInfo.value.web_logo
        if (!document.querySelector(`link[rel='apple-touch-icon'][sizes='${size}']`) &&
            !document.querySelector(`link[rel='apple-touch-icon']${size ? '' : ':not([sizes])'}`)) {
            document.head.appendChild(appleLink)
        }
    })

    // 配置添加到主屏幕的应用名称（iOS 在添加到主屏幕时读取当前页面的该 meta）
    let appTitleMeta = document.querySelector("meta[name='apple-mobile-web-app-title']")
    if (!appTitleMeta) {
        appTitleMeta = document.createElement('meta')
        appTitleMeta.name = 'apple-mobile-web-app-title'
        document.head.appendChild(appTitleMeta)
    }
    appTitleMeta.content = webInfo.value.web_name

}

// 清除 URL 中的微信授权 code，避免失效 code 残留导致登录卡死
const clearUrlCode = () => {
    const url = new URL(window.location.href)
    if (url.searchParams.has('code')) {
        url.searchParams.delete('code')
        url.searchParams.delete('state')
        window.history.replaceState({}, '', url.toString())
    }
}

// 发起微信授权跳转（带次数限制，逻辑复用 common）
const wxAuthorize = () => {
    common.wxAuthorize()
}

const weixinLogin = async () => {
    const userAgent = navigator.userAgent.toLowerCase()
    const urlParams = new URLSearchParams(window.location.search)
    const code = urlParams.get('code')
    // 判断微信环境
    if (userAgent.indexOf('micromessenger') > -1) {
        if (token.value == '' && code) {
            // 微信登录
            try {
                const response = await weixin_login({
                    code: code
                })
                if (response && response.code == 1) {
                    // 登录成功，清除 code 并重置授权次数
                    clearUrlCode()
                    common.resetWxAuthCount()
                    token.value = response.data.token
                    uuid.value = response.data.uuid
                    localStorage.setItem('token', response.data.token)
                    localStorage.setItem('uuid', response.data.uuid)
                    localStorage.setItem('nickName', response.data.nick_name)
                    // 获取用户信息
                    const response_ = await user_info({
                        token: response.data.token,
                        uuid: response.data.uuid
                    })
                    if (response_.code == 1) {
                        sessionStorage.setItem('userInfo', JSON.stringify(response_.data))
                        // setTimeout(() => {
                        //     window.location.href = window.location.origin
                        // }, 1000)
                    }
                } else {
                    // 换 token 失败（code 可能已失效）：清除 code 后重新授权
                    clearUrlCode()
                    wxAuthorize()
                }
            } catch (e) {
                // 请求异常：清除 code 后重新授权
                clearUrlCode()
                wxAuthorize()
            }
        } else if (token.value == '' && !code) {
            wxAuthorize()
        }
    }
}

// 获取用户信息
const getUserInfo = async () => {
    if (token.value) {
        const channelUserParams = {
            token: token.value,
            uuid: uuid.value
        }
        try {
            const channelUserResponse = await updateNickName(channelUserParams, false)
        } catch (e) {
        } finally {
            const response = await user_info({
                token: token.value,
                uuid: uuid.value
            })
            sessionStorage.setItem('userInfo', JSON.stringify(response.data))
        }
    }
}

// 初始化客服
const initQiyu = () => {
    YSF.init('a1b7a98d2d60abc0cee2c3d0cf621c9f', { hidden: 1 }).then(ysf => {
        window.ysf = ysf
    }).catch(error => {
        showToast('sdk加载失败---', error)
    })
}
</script>

<style>
@import '../src/assets/css/style.css';

#app {
    height: 100%;
    margin: auto;
}

.pc-container {
    position: relative;
    width: 100%;
    height: 100vh;
    overflow: hidden;
}

.mobile-frame {
    width: 375px;
    height: 100%;
    margin: 0 auto;
    display: block;
    border: none;
}
</style>